/**
 * Change All — trouble-ticket fan-out payload builders.
 *
 * Pure helpers, extracted from TroubleTicketEditModal so the two rules that matter
 * most can be locked by tests without mounting the whole modal:
 *   1. Reschedule Requested IS bulk-creatable — its one question is asked once, up
 *      front, and per-pro eligibility is decided server-side (see below).
 *   2. The per-pro fields (billto, responsibledriver) never leave the anchor.
 *
 * CREATE only — there is no bulk edit or bulk delete of tickets/service failures.
 */
import type {
  TroubleTicketChangeAllTicket,
  TroubleTicketChangeAllSf,
} from '../../../services/troubleTickets.service';
import { SERVICE_FAILURE_TYPE_REQUIRED_MESSAGE } from '../../../constants/serviceFailureTypes';

/**
 * Why the "Apply to all" button is disabled, or null when it's usable.
 *
 * Reschedule Requested IS fannable — the "also remove the scheduled date?" question is
 * asked once and applied to every pro, and each pro resolves its own dispatch group and
 * scheduled date server-side. Per-pro eligibility (an LC whose group still carries a
 * driver, a paid leg, no scheduled date) is decided by the backend and surfaced in the
 * review modal, not guessed at here.
 *
 * `sfTypeMissing` — the embedded service failure carries a controllable (C:) reason and
 * no type. The single-pro save refuses the same combination; blocking here keeps the
 * fan-out from creating N tickets whose service failure then fails on every pro. The
 * backend checks it once more, up front, before any pro is touched.
 */
export const INCOMPLETE_TICKET_BLOCKED = 'Fill in Category, Assigned To and Issue first.';

export function bulkBlockedReason(opts: {
  hasChanges: boolean;
  sfTypeMissing?: boolean;
}): string | null {
  if (!opts.hasChanges) return INCOMPLETE_TICKET_BLOCKED;
  if (opts.sfTypeMissing) return SERVICE_FAILURE_TYPE_REQUIRED_MESSAGE;
  return null;
}

/** The two appointment dates a Reschedule Requested can remove. Either, both or
 *  neither — each is gated on its own scope, per pro. */
export interface RescheduleDateChoice {
  /** The delivery leg's dispatch group. */
  delivery: boolean;
  /** The pickup leg's PICKUP -> STOPOFF drop — the pre-pull. */
  prepull: boolean;
}

export const RESCHEDULE_DELIVERY_LABEL = 'Delivery appointment date';
export const RESCHEDULE_PREPULL_LABEL = 'Pre-pull date';

/**
 * The prompt shown once before a Reschedule Requested fan-out. One answer applied
 * to every pro, so the copy has to say what happens to the pros the answer does
 * not fit — a fan-out cannot stop and ask per order.
 *
 * Both checkboxes are offered unconditionally here, unlike the single-pro dialog
 * which hides a target it knows is unavailable. Availability is per pro (one has
 * a pre-pull, the next does not) and is not known until the preview runs, so the
 * honest thing is to offer both and report per pro in the review modal.
 *
 * The role changes what a box means, not whether it appears. A DISPATCHER's
 * delivery clear resets the driver and arrival/departure times whatever the date
 * answer — the dispatch is being reworked either way. An LC's scopes are already
 * required to be clean, so for them an unticked box writes nothing at all.
 */
export function rescheduleFanoutPrompt(opts: {
  canClearDispatch: boolean;
  proCount: number;
}): {
  title: string;
  body: string;
  deliveryNote: string;
  prepullNote: string;
  footnote: string;
} {
  const pros = `${opts.proCount} pro${opts.proCount === 1 ? '' : 's'}`;
  return {
    title: 'Which appointment dates should be removed?',
    body:
      `Answer once — it applies to all ${pros}. Each pro resolves its own delivery `
      + `dispatch group and its own pre-pull leg, so a pro the answer does not fit is `
      + `reported on the next screen rather than guessed at.`,
    deliveryNote: opts.canClearDispatch
      ? 'Clears the appointment dates across the delivery dispatch group and records '
        + 'a service failure per pro. The driver and arrival/departure times on that '
        + 'group are cleared either way.'
      : 'Clears the appointment dates across the delivery dispatch group and records '
        + 'a service failure per pro. Pros whose dispatch still has a driver or '
        + 'arrival/departure times are skipped — a dispatcher has to clear those first.',
    prepullNote: opts.canClearDispatch
      ? "Clears the pickup leg's appointment and resets its driver and "
        + 'arrival/departure times. Pros with no pre-pull leg, or whose pre-pull is '
        + 'already paid or departed, keep their date and are listed on the next screen.'
      : "Clears the pickup leg's appointment. Pros with no pre-pull leg, or whose "
        + 'pre-pull is still dispatched, keep their date and are listed on the next '
        + 'screen.',
    footnote:
      'No reschedule is recorded for a pre-pull on its own — that record tracks the '
      + 'delivery date moving.',
  };
}

/** The review modal's one-line summary of the answer. */
export function rescheduleFanoutSummary(
  choice: RescheduleDateChoice,
  canClearDispatch: boolean,
): string {
  const removing = [
    choice.delivery ? 'the delivery date' : null,
    choice.prepull ? 'the pre-pull date' : null,
  ].filter(Boolean);
  const dates = removing.length
    ? `removing ${removing.join(' and ')}`
    : 'keeping both appointment dates';
  const dispatch = canClearDispatch
    ? 'the delivery dispatch is cleared on each pro'
    : 'pros with an active dispatch are skipped';
  return `${dates}; ${dispatch}`;
}

/**
 * The ticket to create on every target pro.
 *
 * Deliberately carries no `pronumber` (it comes from the target set) and no `billto`.
 * billto selects the THD EDI handle, so reusing the anchor's would queue EDI events
 * against the wrong customer — the backend reads each pro's own instead.
 */
export function buildBulkTicket(v: {
  category: string;
  issue: string;
  assignedto: string;
  status?: string;
  driverticket?: string;
  preventable?: string | null;
  reason?: string;
  servicefailure?: string | null;
}): TroubleTicketChangeAllTicket {
  return {
    category: v.category,
    issue: v.issue,
    assignedto: v.assignedto,
    status: v.status || 'NEW',
    driverticket: v.driverticket || 'N',
    preventable: String(v.preventable ?? ''),
    reason: v.reason ?? '',
    servicefailure: String(v.servicefailure ?? ''),
  };
}

/**
 * The embedded service failure fanned out with the ticket, or null when the SF section
 * isn't in play.
 *
 * `responsibledriver` is deliberately never included: the driver responsible on the
 * anchor is generally not the driver on the rest of the set, so it stays blank per pro
 * for someone to fill in afterward.
 */
export function buildBulkSf(v: {
  showSfSection: boolean;
  showThdReason: boolean;
  servicefailuredate?: string;
  reason?: string;
  servicefailuretype?: string;
  thd_reason?: string;
  comments?: string;
}): TroubleTicketChangeAllSf | null {
  if (!v.showSfSection) return null;
  return {
    servicefailuredate: v.servicefailuredate || null,
    reason: v.reason ?? '',
    servicefailuretype: v.servicefailuretype ?? '',
    thd_reason: v.showThdReason ? (v.thd_reason ?? '') : '',
    comments: v.comments ?? '',
  };
}
